import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatValue } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, X, Plus, Sparkles } from "lucide-react";
import PlayerTag from "@/components/PlayerTag";

// Reusable no-login trade calculator. `seedA` pre-fills Side A (e.g. the player whose
// page you're on). `heading`/`kicker` let the host page tailor the copy.
export default function MiniTradeCalc({ format = "1qb", seedA = [], heading, kicker }) {
  const [a, setA] = useState(seedA);
  const [b, setB] = useState([]);
  const [active, setActive] = useState(seedA.length ? "b" : "a");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [res, setRes] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  const seedKey = JSON.stringify(seedA);
  useEffect(() => { setA(seedA); setB([]); setRes(null); setActive(seedA.length ? "b" : "a"); /* eslint-disable-next-line */ }, [seedKey]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/public/trade/search", { params: { q } }).then(r => setResults(r.data.players || [])).catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const add = (p) => {
    const cur = active === "a" ? a : b;
    const other = active === "a" ? b : a;
    if (other.find(x => x.player_id === p.player_id)) { toast.error(`${p.name} is already on the other side`); return; }
    const setter = active === "a" ? setA : setB;
    if (!cur.find(x => x.player_id === p.player_id)) setter([...cur, p]);
    setQ(""); setResults([]); setRes(null);
  };
  const rm = (side, id) => {
    (side === "a" ? setA : setB)((side === "a" ? a : b).filter(x => x.player_id !== id));
    setRes(null);
  };

  const evaluate = async () => {
    if (!a.length || !b.length) return;
    setEvaluating(true);
    try {
      const r = await api.post("/public/trade/evaluate", { side_a: a.map(x => x.player_id), side_b: b.map(x => x.player_id) }, { params: { format } });
      setRes(r.data);
    } catch { toast.error("Couldn't evaluate — try different players."); }
    finally { setEvaluating(false); }
  };

  const Chips = ({ side, list }) => (
    <div className="flex flex-wrap gap-1.5 mt-2 min-h-[28px]">
      {list.map(p => (
        <span key={p.player_id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white/5 border border-[var(--border)]" data-testid={`mini-chip-${side}-${p.player_id}`}>
          {p.name}
          <button onClick={() => rm(side, p.player_id)} className="text-[var(--text-3)] hover:text-red-400"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );

  const winText = res ? (res.winner === "side_a" ? "Side A wins" : res.winner === "side_b" ? "Side B wins" : "Even trade") : "";

  return (
    <div className="card-bordered p-6" data-testid="mini-calc">
      <div className="text-[10px] tracking-widest text-[var(--accent)] font-mono mb-1">// {kicker || "DISAGREE? RUN YOUR OWN"}</div>
      <h2 className="font-display text-xl font-bold mb-4">{heading || "Build your own trade — free, no login"}</h2>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        {["a", "b"].map(side => (
          <button key={side} onClick={() => setActive(side)} data-testid={`mini-side-${side}`}
            className={`text-left rounded-md border p-3 transition-colors ${active === side ? "border-[var(--accent)]" : "border-[var(--border)] hover:border-white/30"}`}>
            <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono">SIDE {side.toUpperCase()} GIVES {active === side ? "· ADDING" : ""}</div>
            <Chips side={side} list={side === "a" ? a : b} />
          </button>
        ))}
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-3)]" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search a player to add to Side ${active.toUpperCase()}…`} data-testid="mini-search" className="input-dark w-full !pl-10" />
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full card-bordered overflow-hidden">
            {results.map(p => (
              <button key={p.player_id} onClick={() => add(p)} data-testid={`mini-add-${p.player_id}`} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white/5 text-left">
                <PlayerTag player={p} size={24} link={false} showPos />
                <Plus className="w-3.5 h-3.5 text-[var(--text-3)] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      <button onClick={evaluate} disabled={!a.length || !b.length || evaluating} data-testid="mini-evaluate" className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50">
        {evaluating ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4" />} Evaluate trade
      </button>

      {res && (
        <div className="mt-4 rounded-md border border-[var(--border)] bg-black/30 p-4" data-testid="mini-result">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className={`font-display text-2xl font-black uppercase ${res.winner === "even" ? "text-[var(--cyan)]" : "text-[var(--accent)]"}`}>{winText}</div>
            <div className="font-mono text-sm">Fairness <span className="text-white font-semibold">{res.fairness}%</span> · Diff {res.diff >= 0 ? "+" : ""}{formatValue(res.diff)}</div>
          </div>
          <div className="text-xs text-[var(--text-3)] mt-1">Side A {formatValue(res.side_a.value)} · Side B {formatValue(res.side_b.value)} (DME)</div>
          {res.reason && <div className="text-xs text-[var(--text-2)] mt-1.5" data-testid="mini-reason">{res.reason}</div>}
          <Link to="/login?signup=1" className="btn-secondary text-xs inline-flex items-center gap-1.5 mt-3" data-testid="mini-result-cta">Unlock AI pitches & roster-aware trades — free</Link>
        </div>
      )}
    </div>
  );
}
